import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { motion, AnimatePresence } from "framer-motion";
import { Send, RotateCcw, Sparkles, MessagesSquare, BookOpen, ClipboardList, GraduationCap } from "lucide-react";
import { toast } from "sonner";

// Visual config per mode — kept in sync with backend keys.
const MODES = [
  { key: "quizme",    label: "Quiz Me",   blurb: "Drills one tough question at a time.",                Icon: ClipboardList,   color: "#5AC8FA", glow: "rgba(90,200,250,0.5)",  starter: "Quiz me. Start easy." },
  { key: "socratic",  label: "Socratic",  blurb: "Guides you to the answer through questions.",         Icon: MessagesSquare,  color: "#B8A4FF", glow: "rgba(184,164,255,0.5)", starter: "Help me understand the hardest concept here." },
  { key: "explainer", label: "Explainer", blurb: "Breaks down hard concepts in plain English.",         Icon: BookOpen,        color: "#7C5CFF", glow: "rgba(124,92,255,0.5)",  starter: "Explain the most exam-worthy idea simply." },
  { key: "coach",     label: "Coach",     blurb: "Builds a study plan and keeps you accountable.",      Icon: GraduationCap,   color: "#FCA5A5", glow: "rgba(252,165,165,0.5)", starter: "Build me a 30-minute study plan for this." },
];

export default function TutorChat({ scId }) {
  const [mode, setMode] = useState("explainer");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [resetting, setResetting] = useState(false);
  const scrollerRef = useRef(null);
  const meta = MODES.find((m) => m.key === mode);

  const loadHistory = (nextMode) => {
    api.get(`/studies/${scId}/tutor/history`, { params: { mode: nextMode } })
       .then((r) => setMessages(r.data.messages || []))
       .catch(() => setMessages([]));
  };

  useEffect(() => { loadHistory(mode); /* eslint-disable-next-line */ }, [scId, mode]);

  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages, busy]);

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || busy) return;
    setBusy(true);
    setInput("");
    // Optimistic user bubble
    const tmpId = `t_${Date.now()}`;
    setMessages((m) => [...m, { id: tmpId, role: "user", content, _pending: true }]);
    try {
      const { data } = await api.post(`/studies/${scId}/tutor/message`,
        { mode, content }, { timeout: 60000 });
      setMessages((m) => {
        const without = m.filter((x) => x.id !== tmpId);
        return [...without, data.user, data.assistant];
      });
    } catch (e) {
      setMessages((m) => m.filter((x) => x.id !== tmpId));
      toast.error(e?.response?.data?.detail || "Tutor failed — try again");
    } finally {
      setBusy(false);
    }
  };

  const onReset = async () => {
    if (resetting) return;
    setResetting(true);
    try {
      await api.delete(`/studies/${scId}/tutor/history`, { params: { mode } });
      setMessages([]);
    } catch {/*ignore*/}
    finally { setResetting(false); }
  };

  return (
    <div className="grid lg:grid-cols-[260px_1fr] gap-6" data-testid="tutor-chat">
      {/* Mode rail */}
      <aside>
        <div className="eyebrow mb-3">PERSONA</div>
        <div className="flex lg:flex-col gap-2">
          {MODES.map((m) => {
            const Icon = m.Icon;
            const active = mode === m.key;
            return (
              <button
                key={m.key}
                onClick={() => setMode(m.key)}
                data-testid={`tutor-mode-${m.key}`}
                className="relative w-full text-left px-3.5 py-3 rounded-xl border transition-all group"
                style={{
                  background: active ? `linear-gradient(135deg, ${m.color}1F, transparent 70%)` : "rgba(255,255,255,0.02)",
                  borderColor: active ? `${m.color}55` : "rgba(255,255,255,0.06)",
                  boxShadow: active ? `0 0 22px -8px ${m.glow}, inset 0 0 0 1px ${m.color}33` : "none",
                }}
              >
                <div className="flex items-center gap-2.5">
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                    style={{ background: `${m.color}1A`, color: m.color }}
                  >
                    <Icon size={15} />
                  </div>
                  <div>
                    <div
                      className={`text-[13.5px] font-semibold ${active ? "" : "text-[#E5E5E7]"}`}
                      style={active ? { color: m.color } : undefined}
                    >{m.label}</div>
                    <div className="text-[11.5px] text-[#8E8B99] leading-tight mt-0.5 line-clamp-2 hidden lg:block">{m.blurb}</div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        <button
          onClick={onReset}
          disabled={resetting || messages.length === 0}
          className="mt-5 w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-[12px] text-[#B0AEBC] border border-white/5 hover:bg-white/5 hover:text-white disabled:opacity-40 transition"
          data-testid="tutor-reset-btn"
        >
          <RotateCcw size={12} /> {resetting ? "Resetting…" : "Reset chat"}
        </button>
      </aside>

      {/* Conversation */}
      <section
        className="surface rounded-2xl flex flex-col overflow-hidden"
        style={{
          minHeight: "min(70vh, 640px)",
          boxShadow: `0 0 32px -16px ${meta.glow}`,
        }}
      >
        <header className="flex items-center justify-between px-5 py-3.5 border-b border-white/5">
          <div className="flex items-center gap-2.5">
            <div
              className="w-7 h-7 rounded-md flex items-center justify-center"
              style={{ background: `${meta.color}1A`, color: meta.color }}
            >
              <meta.Icon size={14} />
            </div>
            <div>
              <div className="text-[13.5px] font-semibold text-white">{meta.label}</div>
              <div className="text-[11px] text-[#8E8B99]">{meta.blurb}</div>
            </div>
          </div>
          <div
            className="inline-flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.18em] font-bold text-[#8E8B99]"
            title="Replies are grounded in this study's notes + summary"
          >
            <Sparkles size={11} className="text-[#B8A4FF]" /> Grounded
          </div>
        </header>

        <div ref={scrollerRef} className="flex-1 overflow-y-auto p-5 md:p-7 space-y-5">
          {messages.length === 0 && !busy && (
            <EmptyState meta={meta} onStart={() => send(meta.starter)} />
          )}

          <AnimatePresence initial={false}>
            {messages.map((msg) => (
              <Bubble key={msg.id} msg={msg} accent={meta.color} />
            ))}
          </AnimatePresence>

          {busy && <ThinkingBubble accent={meta.color} />}
        </div>

        {/* Composer */}
        <form
          onSubmit={(e) => { e.preventDefault(); send(); }}
          className="border-t border-white/5 p-3.5 flex items-end gap-2.5 bg-[#0E0E11]/60"
        >
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
            }}
            disabled={busy}
            rows={1}
            placeholder={`Ask ${meta.label.toLowerCase()} anything about this study…`}
            className="flex-1 resize-none bg-black/40 border border-white/10 rounded-xl px-4 py-2.5 text-[14px] leading-relaxed focus:outline-none focus:border-[#7C5CFF] transition-colors max-h-32 disabled:opacity-50"
            data-testid="tutor-input"
            style={{ minHeight: 44 }}
          />
          <button
            type="submit"
            disabled={busy || !input.trim()}
            data-testid="tutor-send-btn"
            className="shrink-0 inline-flex items-center justify-center w-11 h-11 rounded-xl text-white font-semibold transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            style={{
              background: `linear-gradient(135deg, ${meta.color}, #7C5CFF)`,
              boxShadow: busy ? "none" : `0 0 18px -4px ${meta.glow}`,
            }}
          >
            <Send size={15} />
          </button>
        </form>
      </section>
    </div>
  );
}

function EmptyState({ meta, onStart }) {
  const Icon = meta.Icon;
  return (
    <div className="text-center py-10">
      <div
        className="mx-auto w-14 h-14 rounded-2xl flex items-center justify-center mb-5"
        style={{
          background: `linear-gradient(135deg, ${meta.color}, #7C5CFF)`,
          boxShadow: `0 0 40px -8px ${meta.glow}`,
        }}
      >
        <Icon size={22} className="text-white" />
      </div>
      <h3 className="text-[18px] font-semibold mb-1">{meta.label}</h3>
      <p className="text-[13px] text-[#B0AEBC] max-w-sm mx-auto mb-5">{meta.blurb}</p>
      <button
        onClick={onStart}
        data-testid="tutor-starter-btn"
        className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-[12.5px] font-semibold bg-white/[0.04] border border-white/10 hover:bg-white/10 transition-colors"
      >
        <Sparkles size={12} style={{ color: meta.color }} /> &ldquo;{meta.starter}&rdquo;
      </button>
    </div>
  );
}

function Bubble({ msg, accent }) {
  const isUser = msg.role === "user";
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className={`flex ${isUser ? "justify-end" : "justify-start"}`}
    >
      <div
        className={`max-w-[85%] md:max-w-[78%] rounded-2xl px-4 py-3 text-[14px] leading-[1.65] ${
          isUser ? "rounded-tr-md text-white" : "rounded-tl-md text-[#E5E5E7] bg-white/[0.04] border border-white/[0.06]"
        }`}
        style={
          isUser
            ? { background: "linear-gradient(135deg, #7C5CFF, #5AC8FA)", boxShadow: "0 6px 18px -8px rgba(124,92,255,0.55)" }
            : { boxShadow: `inset 0 0 0 1px ${accent}10` }
        }
      >
        {isUser ? (
          <div className="whitespace-pre-wrap">{msg.content}</div>
        ) : (
          <div className="prose-tutor">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
          </div>
        )}
      </div>
    </motion.div>
  );
}

function ThinkingBubble({ accent }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex justify-start"
      data-testid="tutor-thinking"
    >
      <div
        className="rounded-2xl rounded-tl-md px-4 py-3 bg-white/[0.04] border border-white/[0.06] flex items-center gap-2"
      >
        {[0, 1, 2].map((i) => (
          <motion.span
            key={i}
            className="w-1.5 h-1.5 rounded-full"
            style={{ background: accent }}
            animate={{ opacity: [0.25, 1, 0.25], y: [0, -3, 0] }}
            transition={{ duration: 0.9, repeat: Infinity, delay: i * 0.15 }}
          />
        ))}
      </div>
    </motion.div>
  );
}
